'use client';

import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'react-toastify';
import { AIR_REQUESTS_QUERY_KEY } from '@/app/(main)/client-detail/consts/client-detail';
import { useDeleteAirRequestMutation } from '@/app/(main)/client-detail/hooks/useDeleteAirRequestMutation';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import BackArrowButton from '/public/icons/back-arrow-button.svg';
import LoaderSmall from '/public/icons/loader-small.svg';

interface DeleteDraftModalProps {
  draftId: number | null;
  onGoBack: VoidFunction;
  onSelectDraft: VoidFunction;
}

export const DeleteDraftModal: React.FC<DeleteDraftModalProps> = ({ draftId, onGoBack, onSelectDraft }) => {
  const queryClient = useQueryClient();
  const { mutate: deleteAirRequest, isPending: isDeleting } = useDeleteAirRequestMutation();

  const handleDelete = () => {
    if (draftId) {
      deleteAirRequest(
        { id: draftId },
        {
          onSuccess: () => {
            toast.success('Air request deleted successfully');
            onGoBack();
            onSelectDraft();
            queryClient.invalidateQueries({ queryKey: [AIR_REQUESTS_QUERY_KEY] });
          },
        }
      );
    }
  };

  return (
    <div className="py-11.5 px-9 flex flex-col w-113.5 max-md:px-4 max-md:pt-6 max-md:pb-4">
      <h2 className="light text-blue-900 mb-[6px] text-center max-w-[260px] self-center">
        Are you sure you want to delete this draft?
      </h2>
      <p className="text-body-regular text-grey-700 text-center mb-9 max-md:mb-6 max-md:text-sm max-md:leading-[150%]">
        This action will permanently remove the draft and its information. It cannot be undone.
      </p>

      <div className="flex gap-5 w-full max-md:gap-4">
        <BaseButton
          intent="primary"
          onClick={onGoBack}
          size="full"
          disabled={isDeleting}
          className="max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:px-2 max-md:text-sm"
          startIcon={<BackArrowButton className="text-white fill-current" />}
        >
          No, Go Back
        </BaseButton>
        <BaseButton
          intent="danger"
          onClick={handleDelete}
          size="full"
          disabled={isDeleting}
          className="max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:px-2 max-md:text-sm"
          startIcon={isDeleting ? <LoaderSmall className="w-5 h-5 text-white animate-spin" /> : undefined}
        >
          {isDeleting ? 'Deleting...' : 'Delete Draft'}
        </BaseButton>
      </div>
    </div>
  );
};
